import { type JSX, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import useAuthorization from '@alltrails/context/hooks/useAuthorization';
import { CustomFilterChip } from '@alltrails/denali/components/FilterBar';
import AdminFiltersModal from '../components/AdminFiltersModal/AdminFiltersModal';
import { useLazyGetPlacesQuery } from '../redux/exploreServiceApi';
import { ExploreServiceState, updateAdminFilters } from '../redux/exploreServiceSlice';
import AdminExploreFilters, { AdminExploreFilterLocations, AdminExploreFilterLocationType } from '../types/AdminExploreFilters';
import { BoundingBoxLocation } from '../types/ExploreServiceLocation';
import useAdminClosedFilterConfig from './filterConfigs/useAdminClosedFilterConfig';
import useAdminLocationFilterConfig from './filterConfigs/useAdminLocationFilterConfig';
import useExploreServiceFilterCounts from './useExploreServiceFilterCounts';

// Just a convenience hook to pull code out of ExploreFiltersBar - this logic is tested by that component
const useAdminFiltersModal = (): { adminFiltersChip?: CustomFilterChip; adminFiltersModal?: JSX.Element } => {
  const intl = useIntl();
  const { hasPermission } = useAuthorization();
  const dispatch = useDispatch();

  const { adminFilters, boundingBox, mapRotation, searchIsLoading, searchResultsSummary } = useSelector(
    (state: { exploreService: ExploreServiceState }) => state.exploreService
  );
  const { adminFilterCount } = useExploreServiceFilterCounts();

  const appliedLocations = useMemo(() => {
    const locations: AdminExploreFilterLocations = { area: [], city: [], country: [], state: [] };
    (Object.keys(locations) as AdminExploreFilterLocationType[]).forEach(locationType => {
      adminFilters[locationType]?.forEach(location => {
        locations[locationType].push(location);
      });
    });
    return locations;
  }, [adminFilters]);

  const [locations, setLocations] = useState<AdminExploreFilterLocations>(appliedLocations);

  const updateLocations = useCallback(
    (newLocations: AdminExploreFilterLocations) => {
      // We fetch locations based on the current map area but still want to display selected locations that may be outside of the map area
      (Object.keys(appliedLocations) as AdminExploreFilterLocationType[]).forEach(locationType => {
        const newIds = newLocations[locationType].map(({ id }) => id);
        appliedLocations[locationType]?.forEach(location => {
          if (!newIds.includes(location.id)) {
            newLocations[locationType].push(location);
          }
        });

        newLocations[locationType].sort((a, b) => a.name.localeCompare(b.name));
      });

      setLocations(newLocations);
    },
    [appliedLocations]
  );

  const [getPlaces] = useLazyGetPlacesQuery();

  const [isAdminFiltersModalOpen, setIsAdminFiltersModalOpen] = useState(false);
  const onAdminFiltersCloseRequest = useCallback(() => {
    setIsAdminFiltersModalOpen(false);
  }, []);

  // Just used to compare the new filters to the old filters to determine if the filters changed.
  const previousFilters = useRef(adminFilters);
  useEffect(() => {
    previousFilters.current = adminFilters;
  }, [adminFilters]);

  const onAdminFiltersChange = useCallback(
    (adminFilters: AdminExploreFilters) => {
      if (JSON.stringify(previousFilters.current) === JSON.stringify(adminFilters)) {
        return;
      }
      dispatch(updateAdminFilters(adminFilters));
    },
    [dispatch]
  );

  const closedFilterConfig = useAdminClosedFilterConfig(adminFilters, onAdminFiltersChange);
  const areaFilterConfig = useAdminLocationFilterConfig('area', locations.area, adminFilters, onAdminFiltersChange);
  const cityFilterConfig = useAdminLocationFilterConfig('city', locations.city, adminFilters, onAdminFiltersChange);
  const countryFilterConfig = useAdminLocationFilterConfig('country', locations.country, adminFilters, onAdminFiltersChange);
  const stateFilterConfig = useAdminLocationFilterConfig('state', locations.state, adminFilters, onAdminFiltersChange);

  if (!hasPermission({ permission: 'trails:manage' }) || !boundingBox) {
    return { adminFiltersChip: undefined, adminFiltersModal: undefined };
  }

  const fetchNewPlaces = () => {
    const location: BoundingBoxLocation = { mapRotation, ...boundingBox };
    return getPlaces({ location, limit: 10 }).then(({ data }) => {
      const newLocations: AdminExploreFilterLocations = { area: [], city: [], country: [], state: [] };
      data?.searchResults.forEach(({ location_type, ID, name }) => {
        if (location_type === 'area' || location_type === 'city' || location_type === 'country' || location_type === 'state') {
          newLocations[location_type].push({ id: ID, name, type: location_type });
        }
      });
      updateLocations(newLocations);
    });
  };

  const adminText = intl.formatMessage({ defaultMessage: 'Admin' });
  const appliedAdminFilterCountText = adminFilterCount ? ` (${adminFilterCount})` : '';

  const adminFiltersChip: CustomFilterChip = {
    type: 'custom-chip',
    id: 'admin-filters',
    filterChip: {
      hideChevron: true,
      onClick: () => {
        fetchNewPlaces().then(() => {
          setIsAdminFiltersModalOpen(true);
        });
      },
      testId: 'admin-filters',
      unselectedText: `${adminText}${appliedAdminFilterCountText}`
    }
  };

  const filterConfigs = [
    closedFilterConfig,
    // Don't display the location filter if there is at most one option and its unselected
    ...[areaFilterConfig, cityFilterConfig, stateFilterConfig, countryFilterConfig].filter(
      ({ checkboxGroup }) => !(checkboxGroup.options.length <= 1 && !checkboxGroup.options[0]?.selected)
    )
  ];

  const adminFiltersModal = (
    <AdminFiltersModal
      onCloseRequest={onAdminFiltersCloseRequest}
      isOpen={isAdminFiltersModalOpen}
      filterConfigs={filterConfigs}
      onClear={
        adminFilterCount
          ? () => {
              onAdminFiltersChange({});
            }
          : undefined
      }
      trailResultCount={searchIsLoading || !searchResultsSummary ? null : searchResultsSummary.count}
    />
  );

  return { adminFiltersChip, adminFiltersModal };
};

export default useAdminFiltersModal;
